import React, {useEffect, useRef, useState} from 'react';
import {CustomCard} from "../../../../../components/UI/AntCustom/CustomCard";
import {CustomSwitch} from "../../../../../components/UI/AntCustom/CustomSwitch";
import styled from "styled-components";
import {defineMessages, useIntl} from "react-intl";
import {
  getNativePreferences,
  isNativeBridgeAvailable,
  setNativePreference,
} from "../../../../../utils/nativeBridge/nativeBridge";

const PhoneSettingsMessages = defineMessages({
  title: {
    id: "phoneSettings.title",
    defaultMessage: "Phone",
  },
  useFaceId: {
    id: "phoneSettings.useFaceId",
    defaultMessage: "Use Face ID",
  },
});

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Label = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: #000000;
`;

const LargerToggleWrapper = styled.div`
  display: inline-flex;
  transform: scale(1.45);
  transform-origin: right center;
`;

/**
 * Phone/app-specific settings (Face ID, etc.). Only render this when isInAppWebView() is true;
 * this component still checks the bridge is available.
 */
export const PhoneSettingsSection: React.FC = () => {
  const intl = useIntl();
  const [useFaceId, setUseFaceId] = useState<boolean>(false);
  const [loaded, setLoaded] = useState(false);
  const setUseFaceIdRef = useRef(setUseFaceId);
  setUseFaceIdRef.current = setUseFaceId;

  useEffect(() => {
    if (!isNativeBridgeAvailable()) return;
    getNativePreferences((prefs) => {
      setUseFaceId(prefs.useFaceId);
      setLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!isNativeBridgeAvailable()) return;
    window.__onNativePreferenceRejected = (key) => {
      if (key === 'useFaceId') {
        // Schedule revert so React reliably applies it when callback runs from native bridge
        const setter = setUseFaceIdRef.current;
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(() => setter(false));
        } else {
          setTimeout(() => setter(false), 0);
        }
      }
    };
    return () => {
      window.__onNativePreferenceRejected = undefined;
    };
  }, []);

  const handleFaceIdChange = (checked: boolean) => {
    setUseFaceId(checked);
    setNativePreference('useFaceId', checked);
  };

  if (!isNativeBridgeAvailable() || !loaded) {
    return null;
  }

  return (
    <CustomCard title={intl.formatMessage(PhoneSettingsMessages.title)} width="max">
      <Row>
        <Label>{intl.formatMessage(PhoneSettingsMessages.useFaceId)}</Label>
        <LargerToggleWrapper>
          <CustomSwitch
            checked={useFaceId}
            onChange={handleFaceIdChange}
          />
        </LargerToggleWrapper>
      </Row>
    </CustomCard>
  );
};
